import { FastForward, Pause, Play, Rewind } from "lucide-react"

import { Button } from "@/components/base/buttons/button"
import { Slider } from "@/components/base/slider/slider"

import { useReplay } from "./ReplayProvider"

export function ReplayControls() {
  const {
    isPlaying,
    play,
    pause,
    elapsed,
    metadata,
    playbackSpeed,
    setSpeed,
    seek,
    currentEventIndex,
    events,
  } = useReplay()

  const duration = metadata?.duration_ms ?? 0
  const progress = duration > 0 ? (elapsed / duration) * 100 : 0

  const formatTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)

    if (hours > 0) {
      return `${hours}:${String(minutes % 60).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`
    }
    return `${minutes}:${String(seconds % 60).padStart(2, "0")}`
  }

  const speedItems = [
    { id: "0.5", label: "0.5x" },
    { id: "1", label: "1x" },
    { id: "1.5", label: "1.5x" },
    { id: "2", label: "2x" },
    { id: "3", label: "3x" },
  ]

  const handleSeekChange = (value: number | number[]) => {
    const arr = Array.isArray(value) ? value : [value]
    if (!arr?.length) return
    seek(arr[0])
  }

  const skipBackward = () => {
    const newIndex = Math.max(0, currentEventIndex - 10)
    const newProgress = (newIndex / events.length) * 100
    seek(newProgress)
  }

  const skipForward = () => {
    const newIndex = Math.min(events.length - 1, currentEventIndex + 10)
    const newProgress = (newIndex / events.length) * 100
    seek(newProgress)
  }

  const currentStage =
    events[currentEventIndex]?.stage ??
    metadata?.initial_state?.stage ??
    "Unknown"

  return (
    <div className="replay-controls rounded-xl bg-white shadow-md dark:bg-gray-900">
      <div className="px-8 py-6">
        {/* Playback Controls - Top */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Skip backward */}
            <Button
              color="tertiary"
              size="md"
              onClick={skipBackward}
              isDisabled={currentEventIndex === 0}
              className="h-10 w-10 rounded-full p-0"
            >
              <Rewind className="h-4 w-4" />
            </Button>

            {/* Play/Pause */}
            <Button
              color="primary"
              size="lg"
              onClick={isPlaying ? pause : play}
              className="h-12 w-12 rounded-full p-0"
            >
              {isPlaying ? (
                <Pause className="h-5 w-5" />
              ) : (
                <Play className="ml-0.5 h-5 w-5" />
              )}
            </Button>

            {/* Skip forward */}
            <Button
              color="tertiary"
              size="md"
              onClick={skipForward}
              isDisabled={currentEventIndex >= events.length - 1}
              className="h-10 w-10 rounded-full p-0"
            >
              <FastForward className="h-4 w-4" />
            </Button>
          </div>

          {/* Speed selector - Minimal */}
          <button
            onClick={() => {
              const currentIndex = speedItems.findIndex(
                (item) => item.id === String(playbackSpeed),
              )
              const nextIndex = (currentIndex + 1) % speedItems.length
              setSpeed(parseFloat(speedItems[nextIndex].id))
            }}
            className="rounded-md bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 hover:text-gray-900 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100"
          >
            {playbackSpeed}x
          </button>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <Slider
            value={[progress]}
            onChange={handleSeekChange}
            minValue={0}
            maxValue={100}
            labelPosition="top-floating"
            labelFormatter={(v) => {
              const pct = Array.isArray(v) ? Number(v[0]) : Number(v)
              const ms = isNaN(pct) ? 0 : (pct / 100) * duration
              return `${formatTime(ms)}`
            }}
            className="w-full"
          />
        </div>

        {/* Metadata Bar */}
        <div className="flex items-center justify-between border-t border-gray-200 pt-4 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium tracking-wide text-gray-500 uppercase dark:text-gray-400">
              Event
            </span>
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {currentEventIndex + 1} / {events.length}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium tracking-wide text-gray-500 uppercase dark:text-gray-400">
              Stage
            </span>
            <span className="text-sm font-semibold text-gray-900 capitalize dark:text-gray-100">
              {currentStage.replace(/_/g, " ")}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
